/**
 * Settings → Placements & prices → Placement map: the DEFAULT map every
 * campaign starts from. One tab per page; each page is a stack of section
 * strips. Click a section to rename / move / replace / add a frame; click a
 * frame to link it to what it sells, drag it, resize it, type its numbers.
 */
import { useMemo, useState } from 'react';
import { Button } from '../ui/Button';
import { CommitInput } from '../ui/CommitInput';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Icon } from '../ui/Icon';
import { useToast } from '../ui/Toast';
import SectionStack from './SectionStack';
import { useCropper } from './useCropper';
import { MapFrame, pastedPicture, readPicture, useMapMutations, useMapPages } from '../../lib/api/placementMap';
import { reachLine, useReachSummary } from '../../lib/api/deals';
import { useRates } from '../../lib/api/rateCard';
import { ASSET_CATEGORIES } from '../../lib/types';

const STYLES = `
.me { display: grid; grid-template-columns: minmax(0, 1fr) 340px; gap: 18px; align-items: start; }
@media (max-width: 1000px) { .me { grid-template-columns: 1fr; } }
.me-intro { font-size: 13px; color: var(--ink-3); line-height: 1.55; margin: 0 0 12px; }
.me-pages { display: flex; gap: 6px; margin-bottom: 12px; flex-wrap: wrap; }
.me-panel { position: sticky; top: 12px; display: grid; gap: 12px; }
.me-card { padding: 14px 16px; display: grid; gap: 10px; }
.me-card h4 { margin: 0; font-size: 13px; display: flex; align-items: center; gap: 8px; }
.me-card .k { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--ink-4); font-weight: 600; }
.me-card .hint { font-size: 11.5px; color: var(--ink-4); line-height: 1.45; }
.me-card .row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.me .seg { display: inline-flex; gap: 2px; padding: 2px; background: var(--bg-subtle); border: 1px solid var(--border); border-radius: 999px; }
.me .seg button { border: 0; background: transparent; font: inherit; font-size: 12px; font-weight: 600; padding: 4px 11px; border-radius: 999px; color: var(--ink-3); cursor: pointer; }
.me .seg button[aria-pressed="true"] { background: var(--bg-elevated); color: var(--ink); box-shadow: var(--shadow-sm); }
.me .nums { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.me .file { font-size: 12.5px; cursor: pointer; }
.me .danger { color: var(--warn); font-size: 12px; }
`;

function Seg<T extends string>({ value, options, onChange }: { value: T; options: { v: T; l: string }[]; onChange: (v: T) => void }) {
  return (
    <span className="seg">
      {options.map((o) => (
        <button key={o.v} type="button" aria-pressed={value === o.v} onClick={() => value !== o.v && onChange(o.v)}>{o.l}</button>
      ))}
    </span>
  );
}

export default function MapEditor() {
  const toast = useToast();
  const pages = useMapPages();
  const rates = useRates();
  const reach = useReachSummary();
  const m = useMapMutations();
  const { crop, element: cropper } = useCropper();
  const cropExisting = async () => {
    if (!section) return;
    try {
      const pic = await crop({ image: section.image, width: section.width, height: section.height });
      if (pic && pic.image !== section.image) await m.patchSection.mutateAsync({ id: section.id, ...pic });
    } catch (err) {
      fail(err);
    }
  };
  const [pageKey, setPageKey] = useState('home');
  const [sectionId, setSectionId] = useState<number | null>(null);
  const [frameId, setFrameId] = useState<number | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [confirm, setConfirm] = useState<{ kind: 'section' | 'frame'; id: number; label: string } | null>(null);

  const page = pages.data?.find((p) => p.key === pageKey) ?? pages.data?.[0];
  const sections = useMemo(() => page?.sections ?? [], [page]);
  const section = sections.find((s) => s.id === sectionId) ?? null;
  const frame: MapFrame | null = sections.flatMap((s) => s.frames).find((f) => f.id === frameId) ?? null;

  const fail = (err: unknown) => {
    const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
    toast.push(data ? `Could not save: ${Object.values(data).flat().join(' ')}` : 'Could not save');
  };

  const move = (dir: -1 | 1) => {
    if (!page || !section) return;
    const ids = sections.map((s) => s.id);
    const i = ids.indexOf(section.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    m.reorder.mutate({ page: page.id, ids }, { onError: fail });
  };

  // Screenshot straight from the clipboard (no file to pick).
  const paste = async (use: (f: File) => Promise<void>) => {
    try {
      await use(await pastedPicture());
    } catch (err) {
      toast.push(err instanceof Error ? err.message : 'Could not paste the picture.');
    }
  };

  const addSection = async (file: File | undefined) => {
    if (!file || !page) return;
    try {
      const pic = await crop(await readPicture(file));
      if (!pic) return;
      const created = await m.addSection.mutateAsync({ page: page.id, label: newLabel.trim() || file.name.replace(/\.[^.]+$/, ''), ...pic });
      // Insert it right under the selected section (else at the end).
      if (section) {
        const ids = sections.map((s) => s.id);
        ids.splice(ids.indexOf(section.id) + 1, 0, created.id);
        await m.reorder.mutateAsync({ page: page.id, ids });
      }
      setNewLabel('');
      setSectionId(created.id);
      toast.push('Section added — add a frame on it if it sells something.');
    } catch (err) {
      if (err instanceof Error && !('response' in err)) toast.push(err.message);
      else fail(err);
    }
  };

  const replacePicture = async (file: File | undefined) => {
    if (!file || !section) return;
    try {
      const pic = await crop(await readPicture(file));
      if (!pic) return;
      await m.patchSection.mutateAsync({ id: section.id, ...pic });
      toast.push('Picture replaced. Check the frames still sit right.');
    } catch (err) {
      if (err instanceof Error && !('response' in err)) toast.push(err.message);
      else fail(err);
    }
  };

  const rateOptions = useMemo(() => {
    const list = rates.data ?? [];
    return ASSET_CATEGORIES.map((c) => [c, list.filter((r) => r.category === c)] as const).filter(([, l]) => l.length);
  }, [rates.data]);

  if (pages.isLoading) return <div className="note" style={{ padding: 16 }}>Loading…</div>;
  if (!page) return <div className="note" style={{ padding: 16 }}>No placement map yet.</div>;

  const frameRate = frame?.rate ? rates.data?.find((r) => r.id === frame.rate) : undefined;
  const measured = frameRate ? reachLine(reach.data, frameRate.asset_type, frameRate.category) : null;

  return (
    <div>
      <style>{STYLES}</style>
      <p className="me-intro">
        The default pages every campaign starts from, one picture per section like the Shopify theme. Click a section
        to rename it, move it, replace its picture or choose whether campaigns show it; click a frame to say what it sells, drag it into place or resize it
        from its corner. A campaign can reorder sections, hide them or use its own mockup on its partnership page.
      </p>
      <div className="me-pages">
        {(pages.data ?? []).map((p) => (
          <button
            key={p.key}
            type="button"
            className={`filter-pill ${p.key === page.key ? 'active' : ''}`}
            onClick={() => { setPageKey(p.key); setSectionId(null); setFrameId(null); }}
          >
            {p.title} · {p.sections.length}
          </button>
        ))}
      </div>
      <div className="me">
        <SectionStack
          sections={sections}
          editable
          selectedSectionId={sectionId}
          selectedFrameId={frameId}
          onSelectSection={(id) => { setSectionId(id); setFrameId(null); }}
          onSelectFrame={(f, sid) => { setFrameId(f.id); setSectionId(sid); }}
          onFrameMove={(id, box) => m.patchFrame.mutate({ id, ...box }, { onError: fail })}
        />

        <div className="me-panel">
          {frame && (
            <div className="card me-card" style={{ borderLeft: '3px solid #ffbd09' }}>
              <h4><Icon name="tag" size={13} /> Frame</h4>
              <div>
                <div className="k">Sells</div>
                <select
                  className="input"
                  value={frame.rate ?? ''}
                  onChange={(e) => m.patchFrame.mutate({ id: frame.id, rate: e.target.value ? Number(e.target.value) : null }, { onError: fail })}
                >
                  <option value="">— not linked —</option>
                  {rateOptions.map(([cat, list]) => (
                    <optgroup key={cat} label={cat}>
                      {list.map((r) => <option key={r.id} value={r.id}>{r.label}{r.active ? '' : ' (not sellable)'}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <div className="k">Label on the map (optional)</div>
                <CommitInput value={frame.label} placeholder={frame.rate_label ?? ''} onCommit={(v) => m.patchFrame.mutate({ id: frame.id, label: v }, { onError: fail })} />
              </div>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={frame.one_slot} style={{ marginTop: 3 }}
                  onChange={(e) => m.patchFrame.mutate({ id: frame.id, one_slot: e.target.checked }, { onError: fail })} />
                <span>
                  Shows one slot
                  <span className="hint" style={{ display: 'block' }}>Draw it around one card or tile inside the big frame — tagged “1 slot”, so brands see what they book.</span>
                </span>
              </label>
              <div>
                <div className="k">Numbers for this spot</div>
                <div className="nums">
                  <CommitInput type="number" min={0} value={frame.views_per_week != null ? String(frame.views_per_week) : ''} placeholder="Views / week"
                    onCommit={(v) => m.patchFrame.mutate({ id: frame.id, views_per_week: v === '' ? null : Math.max(0, parseInt(v, 10)) }, { onError: fail })} />
                  <CommitInput type="number" min={0} value={frame.clicks_per_week != null ? String(frame.clicks_per_week) : ''} placeholder="Clicks / week"
                    onCommit={(v) => m.patchFrame.mutate({ id: frame.id, clicks_per_week: v === '' ? null : Math.max(0, parseInt(v, 10)) }, { onError: fail })} />
                </div>
                <div className="hint">
                  {measured ? `Measured on our site: ${measured}.` : 'Nothing measured on our site for this placement yet.'} Squary numbers come later.
                </div>
              </div>
              <button type="button" className="link danger" onClick={() => setConfirm({ kind: 'frame', id: frame.id, label: frame.rate_label ?? 'this frame' })}>
                Remove frame
              </button>
            </div>
          )}

          {section ? (
            <div className="card me-card" style={{ borderLeft: '3px solid var(--sitewide)' }}>
              <h4><Icon name="image" size={13} /> Section</h4>
              <CommitInput value={section.label} onCommit={(v) => v.trim() && m.patchSection.mutate({ id: section.id, label: v.trim() }, { onError: fail })} />
              <div className="row">
                <Button variant="ghost" onClick={() => move(-1)} disabled={sections[0]?.id === section.id}>↑ Up</Button>
                <Button variant="ghost" onClick={() => move(1)} disabled={sections[sections.length - 1]?.id === section.id}>↓ Down</Button>
                <Button
                  variant="ghost"
                  onClick={() => m.addFrame.mutate(
                    { section: section.id, x: 10, y: 10, w: 40, h: 60 },
                    { onSuccess: (f) => setFrameId(f.id), onError: fail },
                  )}
                >
                  <Icon name="plus" size={12} /> Add a frame
                </Button>
              </div>
              <div>
                <div className="k">On campaign pages</div>
                <Seg
                  value={section.visible_by_default ? 'shown' : 'off'}
                  options={[{ v: 'shown', l: 'Shown by default' }, { v: 'off', l: 'Off by default' }]}
                  onChange={(v) => m.patchSection.mutate({ id: section.id, visible_by_default: v === 'shown' }, { onError: fail })}
                />
                <div className="hint" style={{ marginTop: 4 }}>
                  {section.visible_by_default
                    ? 'Every campaign shows it; a campaign can still hide it.'
                    : 'Hidden unless a campaign switches it on (Placements tab → Website pages).'}
                </div>
              </div>
              <div className="row">
                <label className="link file">
                  Replace the picture…
                  <input type="file" accept="image/*" hidden onChange={(e) => { const input = e.currentTarget; replacePicture(input.files?.[0]).finally(() => { input.value = ''; }); }} />
                </label>
                <button type="button" className="link file" onClick={() => paste(replacePicture)}>or paste from clipboard</button>
                <button type="button" className="link file" onClick={cropExisting}>Crop</button>
              </div>
              <button type="button" className="link danger" onClick={() => setConfirm({ kind: 'section', id: section.id, label: section.label })}>
                Delete section
              </button>
            </div>
          ) : (
            <div className="card me-card">
              <div className="hint">Click a section on the page to edit it, or a frame to say what it sells.</div>
            </div>
          )}

          <div className="card me-card">
            <h4><Icon name="plus" size={13} /> Add a section</h4>
            <div className="hint">
              A screenshot of one section (e.g. a promotional banner from the theme preview), at desktop width. It goes
              {section ? ` right under “${section.label}”` : ' at the bottom'}.
            </div>
            <input className="input" placeholder="Name, e.g. Promotional banner" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
            <div className="row">
              <label className="link file">
                Upload the screenshot…
                <input type="file" accept="image/*" hidden onChange={(e) => { const input = e.currentTarget; addSection(input.files?.[0]).finally(() => { input.value = ''; }); }} />
              </label>
              <button type="button" className="link file" onClick={() => paste(addSection)}>or paste from clipboard</button>
            </div>
          </div>
        </div>
      </div>

      {cropper}
      <ConfirmDialog
        open={!!confirm}
        danger
        title={confirm?.kind === 'section' ? `Delete the section “${confirm?.label}”?` : `Remove the frame for ${confirm?.label}?`}
        description={confirm?.kind === 'section'
          ? 'Its picture and frames go, also from campaigns that show it. This cannot be undone.'
          : 'The spot no longer shows on the map for any campaign.'}
        confirmLabel={confirm?.kind === 'section' ? 'Delete section' : 'Remove frame'}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (!confirm) return;
          const done = { onSuccess: () => { setConfirm(null); if (confirm.kind === 'section') setSectionId(null); setFrameId(null); }, onError: fail };
          if (confirm.kind === 'section') m.deleteSection.mutate(confirm.id, done);
          else m.deleteFrame.mutate(confirm.id, done);
        }}
      />
    </div>
  );
}
